import styled from "styled-components";
import { useState } from "react";
import { Check, X, Info, HousePlus } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

/* Records that an already-blocked room is being used by the owner or an owner relation.
 *
 * The problem this solves is an accounting one, not an operational one. The room is blocked and the
 * occupant is let in with the lock encoder, so the floor is full while every report reads the room
 * as empty — which is exactly the figure head office sees. Marking it creates a real booking at zero
 * rupiah, so the room counts as occupied without ever counting as sold.
 *
 * Its own dialog rather than ConfirmDialog for the same reason BlockRoomDialog is: the typed values
 * have to reach onConfirm, and ConfirmDialog captures its callback when the dialog state is set.
 */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem 1.25rem 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  font-size: 1.1rem;
  font-weight: 800;
  gap: 0.4rem;
  margin: 0 0 0.5rem;
`;

const Body = styled.div`
  color: ${theme.ink700};
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 0.75rem;
`;

/* The explanation, always open rather than behind a hover tooltip: this is a money-affecting action
 * on a shared terminal, and a hover target is unreachable on the tablets the desk actually uses.
 */
const Explainer = styled.div`
  background: #f1f6fd;
  border-left: 3px solid #2f6fbf;
  border-radius: 8px;
  color: #2f4a70;
  font-size: 0.82rem;
  line-height: 1.55;
  margin-bottom: 0.9rem;
  padding: 0.6rem 0.7rem;

  ul {
    margin: 0.4rem 0 0;
    padding-left: 1.1rem;
  }

  li {
    margin-bottom: 0.2rem;
  }
`;

const ExplainerHead = styled.div`
  align-items: center;
  display: flex;
  font-weight: 800;
  gap: 0.35rem;
`;

const Field = styled.label`
  color: ${theme.muted};
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  font-weight: 700;
  gap: 0.25rem;
  margin-bottom: 0.8rem;
`;

const Input = styled.input`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.ink};
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.45rem 0.6rem;
`;

const TextArea = styled.textarea`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.ink};
  font-size: 0.9rem;
  min-height: 60px;
  padding: 0.45rem 0.6rem;
  resize: vertical;
`;

const Hint = styled.span`
  color: #5e6278;
  font-size: 0.72rem;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
`;

const Cancel = styled(Button)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Ok = styled(Button)`
  background: ${theme.gold};
  border: 0;
  color: #ffffff;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/* Formatted from LOCAL parts, never toISOString(): the desk runs at UTC+7, where converting a local
 * midnight back to UTC lands on the previous day — so the "next day" default came out equal to the
 * check-in date and left the confirm button permanently disabled.
 */
const nextDay = (isoDate) => {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  parsed.setDate(parsed.getDate() + 1);
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${parsed.getFullYear()}-${month}-${day}`;
};

const HouseUseDialog = (props) => {
  const { t } = useT();
  const [occupantName, setOccupantName] = useState("");
  const [checkOutDate, setCheckOutDate] = useState(() => nextDay(props.date));
  const [note, setNote] = useState("");

  const ready = occupantName.trim().length > 0 && checkOutDate > props.date;

  return (
    <Backdrop onClick={props.onCancel}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Title>
          <HousePlus size={16} aria-hidden="true" />
          {t("house_use_title")} {props.roomCode}
        </Title>
        <Body>{t("house_use_body")}</Body>

        <Explainer>
          <ExplainerHead>
            <Info size={13} aria-hidden="true" />
            {t("house_use_explainer_title")}
          </ExplainerHead>
          <ul>
            <li>{t("house_use_explainer_occupancy")}</li>
            <li>{t("house_use_explainer_revenue")}</li>
            <li>{t("house_use_explainer_payment")}</li>
            <li>{t("house_use_explainer_checkout")}</li>
          </ul>
        </Explainer>

        <Field>
          {t("house_use_occupant_label")}
          <Input
            value={occupantName}
            placeholder={t("house_use_occupant_placeholder")}
            onChange={(event) => setOccupantName(event.target.value)}
          />
          <Hint>{t("house_use_occupant_hint")}</Hint>
        </Field>

        <Field>
          {t("house_use_check_out_label")}
          <Input
            type="date"
            value={checkOutDate}
            min={nextDay(props.date)}
            onChange={(event) => setCheckOutDate(event.target.value)}
          />
          {/* t() takes a key only — every caller does its own substitution. */}
          <Hint>
            {t("house_use_check_out_hint").replace("%{date}", props.date)}
          </Hint>
        </Field>

        <Field>
          {t("house_use_note_label")}
          <TextArea
            value={note}
            placeholder={t("house_use_note_placeholder")}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        <Actions>
          <Cancel type="button" onClick={props.onCancel}>
            <X size={15} /> {t("confirm_cancel")}
          </Cancel>
          <Ok
            type="button"
            disabled={!ready}
            onClick={() =>
              props.onConfirm({
                occupantName: occupantName.trim(),
                checkOutDate: checkOutDate,
                note: note,
              })
            }
          >
            <Check size={15} /> {t("house_use_confirm")}
          </Ok>
        </Actions>
      </Card>
    </Backdrop>
  );
};

export default HouseUseDialog;
